import ResultsClient from './ResultsClient';
import { HTTP_METHOD } from './consts';

export class ResultsClientWithKeycloak extends ResultsClient {
    /**
     *
     * @param {String} token - keycloak token
     * @param {String} staffId - staff for header used in results caching
     */
    static getRequestHeaders = (token = '', staffId = '') => ({
        ...(token && { authorization: `Bearer ${token}` }),
        ...(staffId && { staff: staffId }),
    });

    /**
     * Get http client config for get results operation
     * @param {Object{}} params get request parameters
     * @param {Array<String>} params{}.studentUids - non-empty list of student ids to get results for
     * @param {Integer} params{}.schoolYear - school year to get results for, optional
     * @param {String} params{}.gradeLevel - grade results are associated with
     * @param {String} params{}.period - results period
     * @param {Boolean} params{}.includeInvalidated - whether invalidated results should be returned
     * @param {Array<String>} params{}.language - results' language
     * @param {String} token - keycloak token for API authorization
     * @param {String} staffId - staffId for an additional header used in results caching.
     * @returns {Object}
     */
    static getResultsRequestConfig(
        { studentUids, schoolYear, gradeLevel, period, includeInvalidated, language } = {},
        token = '',
        staffId = '',
    ) {
        const headers = this.getRequestHeaders(token, staffId);

        return {
            url: this.BASE_URL,
            method: HTTP_METHOD.GET,
            headers,
            params: {
                studentUids: studentUids.join(','),
                schoolYear,
                gradeLevel,
                period,
                includeInvalidated,
                language: language && language.join(','),
            },
        };
    }

    /**
     * Get http client config for create result operation
     * @param {Object{}} data - result data to save
     * @param {String} token - keycloak token for API authorization
     * @returns {Object}
     */
    static getCreateResultRequestConfig(data, token = '') {
        const headers = this.getRequestHeaders(token);

        return {
            url: this.BASE_URL,
            method: HTTP_METHOD.POST,
            headers,
            data,
        };
    }

    /**
     * Get http client config for update result operation
     * @param {Object{}} data - result data to save
     * @param {String} data{}.resultId - server result id required for update
     * @param {String} token - keycloak token for API authorization
     * @returns {Object}
     */
    static getUpdateResultRequestConfig(data, token = '') {
        const headers = this.getRequestHeaders(token);

        return {
            url: `${this.BASE_URL}/${data.resultId}`,
            method: HTTP_METHOD.PUT,
            headers,
            data,
        };
    }

    /**
     * Send request to create result on server from provided data
     * @param {Object} result - result data
     * @returns {Promise}
     */
    async createResult(result) {
        const headers = ResultsClientWithKeycloak.getRequestHeaders(this.token);
        const response = await this.httpClient.post(this.constructor.BASE_URL, result, {
            headers,
        });
        return response || {};
    }

    /**
     * Update server result by id with provided data
     * @param {Object} result - updated result data
     * @param {String} result{}.resultId - server result id required for update
     * @returns {Promise}
     */
    async updateResult(result) {
        const headers = ResultsClientWithKeycloak.getRequestHeaders(this.token);
        const response = await this.httpClient.put(
            `${this.constructor.BASE_URL}/${result.resultId}`,
            result,
            {
                headers,
            },
        );
        return response || {};
    }

    setToken(token) {
        this.token = token;
    }
}

export default ResultsClientWithKeycloak;
